import React, { useMemo } from "react";
import * as R from "ramda";
import styled from "styled-components";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { Tabs, Icon, Button, Popconfirm, Badge, notification, Row } from "antd";
import { withTranslation } from "react-i18next";
import {
  compose,
  withHandlers,
  mapProps,
  branch,
  renderComponent
} from "recompose";
import {
  dispatchOnMount,
  isLoading,
  dispatchOnUnmount
} from "@worcket/common/hocs";

import Notes from "../../../../components/CandidateModal/Notes";
import CandidateModal from "../../../../components/CandidateModal";
import {
  WrapperAction,
  Title
} from "../../../../components/CandidateModal/WrapperAction";
import Messages from "../../../../components/Chat/Messages";
import { Subtitle } from "../../../../components/CandidateDetails";
import {
  FETCH_MATCH_DETAILS,
  fetchMatchDetails,
  RESET_SELECTED_CANDIDATE,
  fetchMatchMessages,
  requestCvUpdate
} from "../../../../store/actions/candidates";
import { CLIENT_CONNECT, DELETE_MESSAGE } from "../../../../store/actions/chat";
import { getScreeningTag } from "../../../../utils/candidates";

import StepPopover from "./StepPopover";
import { getStepDisplayName } from "./selectors";

const Chat = styled.div`
  display: flex;
  height: 400px;
  justify-content: center;

  @media (max-width: 767px) {
    min-height: 0;
  }
`;

const StepName = styled.span`
  font-size: 14px;
  color: #9ca8b1;
`;

const WrapperNotes = styled.div`
  padding-left: 40px;
  padding-right: 24px;
  margin-bottom: 16px;
  border-top: 1px solid #e8e8e8;
`;

const WrapperNote = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 30px;
  margin-bottom: 10px;
  flex-wrap: wrap;
  position: relative;
`;

const CandidateDetails = ({
  t,
  history,
  candidate,
  closeModal,
  loading,
  stepId,
  moveCandidateToStep,
  removeCandidateFromProcess,
  routeMatch,
  location,
  showMoveButton,
  getStepName,
  canChat,
  canMove,
  chatbotStatus,
  chatbotTags,
  availableSteps,
  opportunity,
  steps,
  stepsOrder,
  unreadMessages,
  onNext,
  onPrev,
  onSelect,
  isSelected,
  isRegistered,
  requestCvUpdate,
  requestCvUpdateStatus
}) => {
  const allChatbotTags = useMemo(
    () =>
      R.map(
        R.cond([
          [
            R.propEq("idStep", "SCREENING"),
            R.assoc("title", t("opportunity:screening.title"))
          ],
          [
            R.has("idStep"),
            tag => R.assoc("title", R.path([tag.idStep, "name"], steps), tag)
          ]
        ]),
        R.defaultTo([], chatbotTags)
      ),
    [chatbotTags, steps, t]
  );

  return (
    <CandidateModal
      screening={getScreeningTag(t, candidate.screeningStatus)}
      onSelect={onSelect}
      isSelected={isSelected}
      chatbotStatus={chatbotStatus}
      // TODO revise chatbot tags logic
      chatbotTags={allChatbotTags}
      loading={loading}
      onClose={closeModal}
      actions={[
        canMove && (
          <WrapperAction key="remove" colorHover="orange">
            <Popconfirm
              key="remove"
              placement="topRight"
              title={t("opportunity:process.candidateDetails.confirmRemove")}
              onConfirm={removeCandidateFromProcess}
              okText={t("global:accept")}
              cancelText={t("global:cancel")}
              disabled={loading}
            >
              <Button
                data-test="remove-candidate-btn"
                type="primary"
                icon="minus-circle"
                shape="circle"
                disabled={loading}
              />
            </Popconfirm>
            <Title>{t("opportunity:process.candidateDetails.remove")}</Title>
          </WrapperAction>
        ),
        showMoveButton && canMove && (
          <WrapperAction key="move" colorHover="primary">
            <StepPopover
              disabled={loading}
              matches={[candidate]}
              step={steps[stepId]}
              steps={availableSteps}
              opportunity={opportunity}
              onSubmit={moveCandidateToStep}
              shape="circle"
            ></StepPopover>
            <Title>{t("opportunity:process.candidateDetails.move")}</Title>
          </WrapperAction>
        )
      ]}
      defaultActiveKey={`${routeMatch.url}/info`}
      videoMetadata={candidate.videoMetadata}
      candidate={candidate}
      notes={({ isActive = false }) => (
        <WrapperNotes>
          <Row style={{ position: "relative" }}>
            <WrapperNote>
              <div style={{ marginBottom: 15 }}>
                <Subtitle>
                  {t("opportunity:process.candidateDetails.thisStepNotes")}
                </Subtitle>
                <Icon
                  style={{ marginLeft: 7, marginRight: 7, color: "#9ca8b1" }}
                  type="right"
                />
                <StepName>{getStepName(steps[stepId])}</StepName>
              </div>
              <Notes isActive={isActive} stepId={stepId} steps={steps} />
            </WrapperNote>
          </Row>
        </WrapperNotes>
      )}
      chatTab={
        canChat && (
          <Tabs.TabPane
            disabled={!isRegistered}
              tab={
                <div data-test="candidate-modal-chat-tab">
                  <Badge dot={true} count={unreadMessages} offset={[-9, 1]}>
                    <Icon type="message" />
                  </Badge>
                  <span>{t("opportunity:process.candidateDetails.chat")}</span>
                </div>
              }
            key={`chat`}
            activeKey={`chat`}
          >
            <Chat>
              <Messages key={candidate.id} candidate={candidate} />
            </Chat>
          </Tabs.TabPane>
        )
      }
      location={location}
      history={history}
      steps={steps}
      stepsOrder={stepsOrder}
      showRequestCvUpdateButton={Boolean(
        opportunity.id && candidate.id && isRegistered
      )}
      requestCvUpdateStatus={requestCvUpdateStatus}
      onRequestCvUpdate={() => requestCvUpdate(candidate.id)}
      onNext={onNext}
      onPrev={onPrev}
    />
  );
};

const enhancer = compose(
  withRouter,
  withTranslation(["opportunity", "form", "global"]),
  connect(
    state => {
      const candidate = R.defaultTo({}, state.candidates.selected);
      const opportunity = R.defaultTo({}, state.opportunity.selected);
      const isUrlInvalid = FETCH_MATCH_DETAILS.getStatus(state) === "rejected";
      const unreadMessages = R.pipe(
        R.pathOr([], ["messages", "newMessages"]),
        R.filter(
          R.both(
            R.propEq("idCandidate", candidate.id),
            R.propEq("idOpportunity", opportunity.id)
          )
        ),
        R.length
      )(state);

      return {
        candidate,
        opportunity,
        isUrlInvalid,
        unreadMessages,
        requestCvUpdateStatus: R.propOr(
          "initial",
          "requestCvUpdateStatus",
          candidate
        )
      };
    },
    {
      resetSelectedCandidate: RESET_SELECTED_CANDIDATE.run,
      deleteMessage: DELETE_MESSAGE.run,
      requestCvUpdate
    }
  ),
  mapProps(props => {
    const { updateMatchNotes, createMatchNotes, stepsOrder, stepId } = props;
    const stepIndex = R.findIndex(R.equals(stepId), stepsOrder);
    const isLastStep = stepIndex === stepsOrder.length - 1;
    const nextStepId =
      stepsOrder[R.findIndex(R.equals(stepId), stepsOrder) + 1];

    return {
      ...props,
      currentStepId: stepId,
      nextStepId,
      updateMatchNotes,
      createMatchNotes,
      showMoveButton: !isLastStep
    };
  }),
  withHandlers({
    moveCandidateToStep: ({
      candidate,
      moveMatch,
      closeModal,
      nextStepId,
      currentStepId
    }) => async stepId => {
      moveMatch(
        {
          ...candidate,
          step: currentStepId
        },
        stepId || nextStepId
      );

      closeModal();
    },
    removeCandidateFromProcess: ({
      handleRemoveMatch,
      closeModal,
      deleteMessage,
      opportunity,
      candidate,
      isRegistered
    }) => async () => {
      handleRemoveMatch();

      if (isRegistered) {
        deleteMessage({
          idOpportunity: opportunity.id,
          idCandidate: candidate.id
        });
      }

      closeModal();
    },
    getStepName: ({ t }) => step => {
      return getStepDisplayName(step, t);
    }
  }),
  dispatchOnMount(({ candidateId, isRegistered }) =>
    fetchMatchDetails({ candidateId, isRegistered })
  ),
  dispatchOnMount(({ candidateId, isRegistered }) =>
    isRegistered ? fetchMatchMessages(candidateId) : function() {}
  ),
  dispatchOnUnmount(FETCH_MATCH_DETAILS.clearStatus),
  dispatchOnUnmount(RESET_SELECTED_CANDIDATE.run),
  branch(
    ({ isUrlInvalid }) => isUrlInvalid,
    renderComponent(({ t, returnToProcess }) => {
      notification.error({
        message: t("global:error"),
        description: t("toasts:candidates.loadMatchDetailsRejected")
      });
      returnToProcess();
      return null;
    })
  ),
  isLoading(FETCH_MATCH_DETAILS),
  isLoading(CLIENT_CONNECT, { prop: "chatLoading" })
);

export default enhancer(CandidateDetails);
